<script lang="ts" setup>
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import ExploreFacetDropdown from '@/vue/molecules/recco/explore-facet-dropdown.vue';
import {LibraryContentTypeDTO, LibraryMediaTypeDTO, type ExploreLibraryPartnerDTO} from '@/ts/types/dto/recco/library.dto';
import type {ReccoTopicDTO} from '@/ts/types/dto/recco/topic.dto';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import {contentTypeLabelKey, mediaTypeLabelKey, topicLabelKey} from '@/ts/utils/recco-label-keys';

const props = defineProps<{
  mediaTypes: LibraryMediaTypeDTO[];
  contentTypes: LibraryContentTypeDTO[];
  topics: ReccoTopicDTO[];
  saved: boolean;
  partnerIds: UuidDTO[];
  availableTopics: ReccoTopicDTO[];
  partners: ExploreLibraryPartnerDTO[];
  isActive: boolean;
}>();

const emit = defineEmits<{
  toggleMediaType: [value: LibraryMediaTypeDTO];
  toggleContentType: [value: LibraryContentTypeDTO];
  toggleTopic: [value: ReccoTopicDTO];
  toggleSaved: [];
  togglePartner: [id: UuidDTO];
  clearAll: [];
}>();

const {t: $t} = useI18n();

// Append the active-count suffix via i18n (translatable "{label} ({count})"),
// not a hardcoded parenthesis — consistent with the "Filters (n)" toggle.
const facetLabel = (base: string, count: number): string =>
  count > 0 ? $t('explore_filter_facet_count', {label: base, count}) : base;

interface FacetOption {
  value: string;
  label: string;
  selected: boolean;
}

interface Facet {
  key: string;
  label: string;
  selectedCount: number;
  options: FacetOption[];
  onToggle(value: string): void;
}

const facets = computed<Facet[]>(() => [
  {
    key: 'media-type',
    label: facetLabel($t('explore_filter_media_type'), props.mediaTypes.length),
    selectedCount: props.mediaTypes.length,
    options: Object.values(LibraryMediaTypeDTO).map((value) => ({
      value,
      label: $t(mediaTypeLabelKey[value]),
      selected: props.mediaTypes.includes(value),
    })),
    onToggle: (value): void => { emit('toggleMediaType', value as LibraryMediaTypeDTO); },
  },
  {
    key: 'content-type',
    label: facetLabel($t('explore_filter_content_type'), props.contentTypes.length),
    selectedCount: props.contentTypes.length,
    // No "Program" option: the user-facing (non-medical) Programs don't exist yet, and
    // today's program data is medical licensing plumbing that must not surface here.
    options: Object.values(LibraryContentTypeDTO)
      .filter((value) => value !== LibraryContentTypeDTO.Program)
      .map((value) => ({
        value,
        label: $t(contentTypeLabelKey[value]),
        selected: props.contentTypes.includes(value),
      })),
    onToggle: (value): void => { emit('toggleContentType', value as LibraryContentTypeDTO); },
  },
  {
    key: 'topic',
    label: facetLabel($t('explore_filter_topic'), props.topics.length),
    selectedCount: props.topics.length,
    options: props.availableTopics.map((value) => ({
      value,
      label: $t(topicLabelKey[value]),
      selected: props.topics.includes(value),
    })),
    onToggle: (value): void => { emit('toggleTopic', value as ReccoTopicDTO); },
  },
]);

// A nameless partner chip ("by ") is meaningless — only render partners with a name.
const namedPartners = computed<ExploreLibraryPartnerDTO[]>(() =>
  props.partners.filter((partner) => Boolean(partner.name)));
</script>

<template>
  <div class="explore-filter-bar" role="group" :aria-label="$t('explore_filters')">
    <explore-facet-dropdown
      v-for="facet in facets"
      :key="facet.key"
      :label="facet.label"
      :selected-count="facet.selectedCount"
      :options="facet.options"
      @toggle="facet.onToggle"
    />

    <!-- Partner toggle chips: one per partner, hidden entirely when the org has none -->
    <button
      v-for="partner in namedPartners"
      :key="partner.id"
      type="button"
      class="filter-pill"
      :class="{'filter-pill--active': partnerIds.includes(partner.id)}"
      :aria-pressed="partnerIds.includes(partner.id)"
      @click="emit('togglePartner', partner.id)"
    >
      {{ $t('explore_filter_partner', {partner: partner.name}) }}
    </button>

    <!-- Saved toggle chip -->
    <button
      type="button"
      class="filter-pill"
      :class="{'filter-pill--active': saved}"
      :aria-pressed="saved"
      @click="emit('toggleSaved')"
    >
      {{ $t('explore_filter_saved') }}
    </button>

    <button
      v-if="isActive"
      type="button"
      class="clear-all"
      @click="emit('clearAll')"
    >
      {{ $t('explore_filter_clear_all') }}
    </button>
  </div>
</template>

<style lang="scss" scoped>
.explore-filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  // Band spec: 24px inline / 16px block padding,
  // 12px gap, 12px radius; ui-20 reads as a subtle raised surface over the page.
  gap: 12px;
  padding: 16px 24px;
  background-color: var(--Neutral-UI-neutral-ui-20);
  border-radius: 12px;
}

// Toggle chips (Saved, partners) share the exact pill skin of the facet
// dropdowns — one source of truth in the shared mixin, no per-component overrides.
.filter-pill {
  @include explore-filter-pill;
  display: inline-flex;
  align-items: center;

  &--active {
    @include explore-filter-pill-active;
  }
}

.clear-all {
  @include focus-visible;
  @include font-body-sm-bold;
  // Match the pill height (34px) so activating a filter — which reveals this button —
  // doesn't grow the band's row height.
  display: inline-flex;
  align-items: center;
  height: 34px;
  margin-left: auto;
  padding: 0 var(--Spacing-spacing-3);
  border: none;
  background: none;
  color: var(--Neutral-UI-neutral-ui-100);
  cursor: pointer;
}
</style>
